import { applyCommand, type Command } from "../document/commands.ts";
import type { WorldDocument } from "../document/types.ts";
import { MAX_ZONE_TYPES } from "../document/zones.ts";
import { resolveDoorIntent } from "./door-ops.ts";
import { resolveSetIssueIgnores } from "./issue-ignore-ops.ts";
import { resolveLayoutIntent } from "./layout-ops.ts";
import type { CellPatchIntent, Intent, LiveOp } from "./op-types.ts";
import { resolveCells, type AppliedPatch } from "./resolve-cells.ts";
import { resolveAddEntity, resolveAddProp, resolveRemoveItem, resolveRenameGroup, resolveRenameLayer, resolveRenameMap, resolveSetLayerFaceHeight, resolveSetLayerGroup, resolveSetLayerTerrain, resolveSetMapProperties, resolveSetTerrainName, resolveUpdateEntity, resolveUpdateProp } from "./resolve-items.ts";
import { resolveAddGroup, resolveAddLayer, resolveAddMap, resolveChangeTilesets, resolveMoveLayer, resolveRemoveGroup, resolveRemoveLayer, resolveRemoveMap, resolveSetPlayersAbove } from "./resolve-structure.ts";
import { layerOf, type IntentOutcome, type Resolution, type SkipReason } from "./resolve-types.ts";

/**
 * `resolveOp` (ADR-086 decision 18): an operation turned into the commands `commands.ts` defines, with every
 * before-value read from the document it is given. Both ends hold it: the server resolves against the room's
 * document and every tab against its own copy of that document at the same sequence, and since the function
 * is pure and the documents equal, all get the same commands. The later of two operations therefore decides a
 * key by the order alone, per cell, per item and per field, and no before-value crosses the wire.
 *
 * The intents of an operation are independent and in order: each is resolved against the document the ones
 * before it left, applies, changes nothing or is skipped by itself, and a skipped intent leaves the document
 * as it found it. Nothing here throws for an operation `opOf` accepts: what the document cannot take is a
 * skipped intent with its reason.
 */

export { FIELDS_JSON_MAX, FIELD_KEYS_MAX, LAYER_ITEMS_MAX, MAP_GROUPS_MAX, MAP_LAYERS_MAX, TERRAIN_NAMES_MAX, WORLD_GRID_BYTES_MAX, WORLD_MAPS_MAX } from "./resolve-types.ts";
export type { IntentOutcome, SkipReason } from "./resolve-types.ts";

/**
 * An operation resolved against a document: the commands in the order they apply, each intent's outcome in
 * the operation's order, and the document the commands leave, which is the given document itself when none
 * applied.
 */
export interface ResolvedOp {
  readonly commands: readonly Command[];
  readonly outcomes: readonly IntentOutcome[];
  readonly document: WorldDocument;
}

/**
 * Resolves one intent that is not a cell patch against a document.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns Its commands, none when it changes nothing, or the reason it is skipped.
 */
function resolveIntent(world: WorldDocument, intent: Exclude<Intent, CellPatchIntent>): Resolution {
  switch (intent.k) {
    case "renameMap":
      return resolveRenameMap(world, intent);
    case "renameLayer":
      return resolveRenameLayer(world, intent);
    case "renameGroup":
      return resolveRenameGroup(world, intent);
    case "setLayerGroup":
      return resolveSetLayerGroup(world, intent);
    case "setLayerTerrain":
      return resolveSetLayerTerrain(world, intent);
    case "setLayerFaceHeight":
      return resolveSetLayerFaceHeight(world, intent);
    case "setTerrainName":
      return resolveSetTerrainName(world, intent);
    case "setMapProperties":
      return resolveSetMapProperties(world, intent);
    case "setIssueIgnores":
      return resolveSetIssueIgnores(world, intent);
    case "updateProp":
      return resolveUpdateProp(world, intent);
    case "updateEntity":
      return resolveUpdateEntity(world, intent);
    case "addProp":
      return resolveAddProp(world, intent);
    case "addEntity":
      return resolveAddEntity(world, intent);
    case "removeProp":
    case "removeEntity":
      return resolveRemoveItem(world, intent);
    case "addLayer":
      return resolveAddLayer(world, intent);
    case "removeLayer":
      return resolveRemoveLayer(world, intent);
    case "moveLayer":
      return resolveMoveLayer(world, intent);
    case "setPlayersAbove":
      return resolveSetPlayersAbove(world, intent);
    case "addGroup":
      return resolveAddGroup(world, intent);
    case "removeGroup":
      return resolveRemoveGroup(world, intent);
    case "addMap":
      return resolveAddMap(world, intent);
    case "removeMap":
      return resolveRemoveMap(world, intent);
    case "changeTilesets":
      return resolveChangeTilesets(world, intent);
    case "placeMap":
    case "setGrid":
    case "setMapGroups":
    case "setMapGroup":
    case "setMapDraft":
    case "setMapHost":
    case "setMapShare":
    case "setMapCapacity":
    case "setMapAtmosphere":
    case "setSeam":
    case "removeSeam":
    case "setCrossing":
    case "removeCrossing":
      return resolveLayoutIntent(world, intent);
    case "addDoor":
    case "removeDoor":
    case "updateDoor":
      return resolveDoorIntent(world, intent);
  }
}

/**
 * Why an intent whose commands the document refused is skipped. A zone patch whose layer would hold more types
 * than a byte indexes is refused only once the layer is compacted, so it is named here: `limit`, when the
 * layer's types and the patch's together are more than a layer holds. Anything else is a refusal no rule
 * foresaw.
 *
 * @param world The document the intent was resolved against.
 * @param intent The intent.
 * @returns The reason.
 */
function refusal(world: WorldDocument, intent: Intent): SkipReason {
  if (intent.k !== "cells" || intent.grid !== "zone") {
    return "refused";
  }
  const layer = layerOf(world, intent.map, intent.layer);
  const types = new Set<unknown>(layer?.kind === "zone" ? layer.types : []);
  intent.palette.forEach((value) => value !== null && types.add(value));
  return types.size > MAX_ZONE_TYPES ? "limit" : "refused";
}

/**
 * Resolves an operation against a document.
 *
 * @param document The document; never mutated.
 * @param op The operation, or its intents.
 * @returns The commands, each intent's outcome and the document the commands leave. Applying the commands in
 *   order to `document` with `applyCommand` gives that document and never throws.
 */
export function resolveOp(document: WorldDocument, op: LiveOp | readonly Intent[]): ResolvedOp {
  const list: readonly Intent[] = "list" in op ? op.list : op;
  const commands: Command[] = [];
  const outcomes: IntentOutcome[] = [];
  let current = document;
  for (const intent of list) {
    let resolution: Resolution;
    let next = current;
    try {
      const resolved = intent.k === "cells" ? resolveCells(current, intent) : resolveIntent(current, intent);
      if (typeof resolved === "string" || Array.isArray(resolved)) {
        resolution = resolved as Resolution;
        next = typeof resolved === "string" ? current : (resolved as readonly Command[]).reduce(applyCommand, current);
      } else {
        resolution = (resolved as AppliedPatch).commands;
        next = (resolved as AppliedPatch).document;
      }
    } catch {
      resolution = refusal(current, intent);
    }
    if (typeof resolution === "string") {
      outcomes.push({ status: "skipped", reason: resolution });
    } else if (resolution.length === 0) {
      outcomes.push({ status: "same" });
    } else {
      outcomes.push({ status: "applied" });
      for (const command of resolution) {
        commands.push(command);
      }
      current = next;
    }
  }
  return { commands, outcomes, document: current };
}
